/**
 * Dialóg „Prírez" — ukáže, ako dielec reálne leží na doske, ktorú stroj
 * upne. Otvára sa z riadku dielca (tlačidlo + bunka Prírez) pri dielcoch
 * s CNC operáciou (predrez / združenie / rotácia).
 */

import type { JSX } from "react";

import {
  PrirezLegenda,
  PrirezVisual,
} from "@/components/dielec/PrirezVisual";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { CncStav } from "@/lib/cncPrirez";
import { normalizeNum } from "@/lib/format";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stav: CncStav;
  cistaX: number;
  cistaY: number;
  pocetKs: number;
  kod?: string | null;
  smerVlakien?: string | null;
}

export function PrirezDialog({
  open, onOpenChange, stav, cistaX, cistaY, pocetKs, kod, smerVlakien,
}: Props): JSX.Element {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Prírez — {kod || "dielec"} {normalizeNum(cistaX)}×
            {normalizeNum(cistaY)}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Nákres dielca v príreze — rozmery, vôľa a prístupné hrany.
          </DialogDescription>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          <span className="font-mono text-foreground">{stav.oznacenie}</span>
          {stav.popisOperacie ? ` — ${stav.popisOperacie}` : ""}
          {". "}
          <b>{pocetKs} ks</b> →{" "}
          <b>
            {stav.pocetPrirezov}{" "}
            {stav.pocetPrirezov === 1 ? "prírez" : "prírezov"}
          </b>
          {" (kusovník sa nemení)."}
        </p>

        {/* Každý druh prírezu zvlášť — pri zvyšku ich je viac */}
        <div className="grid gap-4 sm:grid-cols-2">
          {stav.prirezy.map((prirez, i) => (
            <div key={i} className="rounded-md border bg-card p-2">
              <div className="mb-1 text-xs text-muted-foreground">
                {prirez.pocet}× prírez{" "}
                {prirez.kusovVPrireze > 1
                  ? `po ${prirez.kusovVPrireze} kusoch`
                  : "po 1 kuse"}
              </div>
              <PrirezVisual
                prirez={prirez}
                cistaX={cistaX}
                cistaY={cistaY}
                nadmieraXMm={stav.nadmiera.x}
                nadmieraYMm={stav.nadmiera.y}
                kod={kod}
                smerVlakien={smerVlakien}
              />
            </div>
          ))}
        </div>

        <PrirezLegenda />

        {stav.kusovVPrireze >= 3 ? (
          <p className="text-[11px] text-muted-foreground">
            Pri troch a viac kusoch v jednom príreze majú voľnú dlhú hranu len
            krajné kusy — stredné sú obklopené materiálom z oboch strán.
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
